import { keyMeasurementGroup } from "@workspace/api-zod/measurement-review";
import type { MeasurementReviewField } from "./measurement-review";
import { shortMeasurementReviewLabel } from "./measurement-review-label";

const GROUP_LABELS: Record<NonNullable<ReturnType<typeof keyMeasurementGroup>>, string> = {
  extension: "Extension overall sizes",
  footings: "Footings",
  footprint: "Footprint area",
  rooms: "Rooms",
  "external-walls": "External walls",
  doors: "External doors",
  windows: "Windows",
  "internal-openings": "Internal openings",
  "internal-walls": "New internal walls",
  steel: "Steel members",
  roof: "Roofs",
  floor: "Floor construction",
};

/** Legacy evidence can have a location in its full label but no subject record.
 * Remove measurement terms only: never shorten away room numbers, opening refs
 * or steel sections as the compact card labels do.
 */
function evidenceLocation(field: MeasurementReviewField) {
  if (field.custom) return "";
  return (field.original?.label || field.label)
    .replace(/\b(?:(?:overall|clear|cut|bearing|total|internal|external)\s+)?(?:footprint area|sloping area|floor area|bearing length|length|width|height|depth|projection|frontage|breadth|span|perimeter|run|area|footprint|count|number|thickness|rise|mass|weight)\b/gi, " ")
    .replace(/\b(?:maximum|minimum|measured|assumed)\b/gi, " ")
    .replace(/^\s*(?:of|for)\b|\b(?:of|for)\s*$/gi, "")
    .replace(/[()]/g, " ")
    .replace(/^\s*[—–:,-]+|[—–:,-]+\s*$/g, "")
    .replace(/\s+/g, " ").trim();
}

/** Presentation only: retain field references and values. Overall extension
 * sizes and footprint come first; preserve source order within other groups. */
export function groupMeasurementReviewFields(fields: MeasurementReviewField[]) {
  const groups = new Map<string, { id: string; label: string; fields: MeasurementReviewField[] }>();
  // A generic added footing length belongs with the sole identified footing,
  // but must not merge two explicitly different foundations.
  const footingSubjects = new Map(fields.filter(f => !f.removed && f.subjectId &&
    keyMeasurementGroup(f.original?.label || f.label, f.unit) === "footings")
    .map(f => [f.subjectId!, f.subjectLabel || "Footing"]));
  for (const field of fields) {
    if (field.removed) continue;
    const category = field.unit === "m²" && /\bfootprint\b/i.test(field.original?.label || field.label)
      ? "footprint" : keyMeasurementGroup(field.original?.label || field.label, field.unit,
        field.subjectKind === "room" ? "room" : undefined);
    const area = category ? GROUP_LABELS[category] : "Other measurements";
    const misplacedArea = ((category === "footprint" || category === "extension") &&
      field.subjectKind !== "extension") || (category === "floor" && field.subjectKind === "opening");
    let subject = misplacedArea ? evidenceLocation(field) :
      field.subjectLabel?.trim() || (!field.subjectId ? evidenceLocation(field) : "");
    // Equal display names must not merge distinct rooms, openings or members.
    let location = field.subjectId && !misplacedArea
      ? ["id", field.subjectId]
      : subject ? ["label", subject.toLowerCase()] : ["unassigned"];
    if (category === "footings" && (!field.subjectId || footingSubjects.size <= 1)) {
      const sole = [...footingSubjects.entries()][0];
      location = sole ? ["id", sole[0]] : ["footings"];
      subject = sole?.[1] || "Footing";
    }
    const id = JSON.stringify([category || "other", ...location]);
    let group = groups.get(id);
    if (!group) {
      const compactSubject = category === "internal-openings"
        ? shortMeasurementReviewLabel(subject.replace(/\s+area$/i, "")) : subject;
      const budget = Math.max(1, 5 - area.split(/\s+/).length);
      const words = compactSubject.split(/\s+/).filter(Boolean);
      const name = words.length <= budget ? words.join(" ") :
        [...words.slice(0, budget - 1), words[words.length - 1]].join(" ");
      group = { id, label: subject ? `${name} · ${area}` : area, fields: [] };
      groups.set(id, group);
    }
    group.fields.push(field);
  }
  const priority = (group: { id: string }) => {
    const category = JSON.parse(group.id)[0];
    return category === "extension" ? 0 : category === "footprint" ? 1 : 2;
  };
  return [...groups.values()].sort((a, b) => priority(a) - priority(b));
}
